/**
 * The Authorization header for calls to our own Pages Functions.
 *
 * Every /api/* Function used to accept any request from anywhere. Sending an
 * SMS as "DirectAuto", sending email, and provisioning a Supabase user with
 * is_super_admin all worked with no session, no token and no header — proven on
 * 23 August by sending a real SMS from a blank browser tab. The Functions now
 * check the caller, and this is what the app sends so that check passes.
 *
 * It returns the signed-in user's access token, which the Function verifies
 * against Supabase. There is no shared secret in the bundle: a token belongs to
 * one person, expires on its own, and dies when they sign out. A secret baked
 * into the JavaScript would just be the AutoGrab key problem again.
 *
 * Callers must treat a missing session as a failed call rather than sending
 * anonymously — see the `assert` variant.
 */

import { supabase } from './supabase'

/** Headers for an authenticated call, or plain JSON headers if not signed in. */
export async function apiHeaders(
  extra: Record<string, string> = {}
): Promise<Record<string, string>> {
  const base: Record<string, string> = { 'Content-Type': 'application/json', ...extra }
  try {
    const { data } = await supabase.auth.getSession()
    const t = data?.session?.access_token
    if (t) base.Authorization = `Bearer ${t}`
  } catch {
    // Fall through without the header. The Function will refuse, which is the
    // correct outcome — better a clean 401 than a silent unauthenticated send.
  }
  return base
}

/**
 * Same, but refuses to build headers at all without a session.
 *
 * Use this for anything that spends money or changes another account: an
 * unauthenticated attempt should fail here, in one place, rather than travel to
 * the server and rely on it to say no.
 */
export async function apiHeadersOrThrow(
  extra: Record<string, string> = {}
): Promise<Record<string, string>> {
  const h = await apiHeaders(extra)
  if (!h.Authorization) {
    throw new Error('You are signed out. Sign in again and retry.')
  }
  return h
}
